import { CANVAS_CONSTANTS } from './constants.js';

/**
 * Canvas interaction handler for mouse and touch events
 */
export class CanvasInteractionHandler {
    /**
     * Create interaction handler
     * @param {HTMLCanvasElement} canvasElement - Canvas element
     * @param {CanvasRenderer} renderer - Canvas renderer instance
     * @param {Object} config - Configuration object
     */
    constructor(canvasElement, renderer, config = {}) {
        this.canvas = canvasElement;
        this.renderer = renderer;
        this.config = config;
        this.viewport = renderer.getViewportTransform();

        // Interaction state
        this.isDragging = false;
        this.lastMousePos = { x: 0, y: 0 };
        this.lastTouchDistance = 0;
        this.lastTouchCenter = { x: 0, y: 0 };

        // Event handlers (bound to this context)
        this.boundHandlers = {
            mouseDown: this.onMouseDown.bind(this),
            mouseMove: this.onMouseMove.bind(this),
            mouseUp: this.onMouseUp.bind(this),
            mouseWheel: this.onMouseWheel.bind(this),
            doubleClick: this.onDoubleClick.bind(this),
            touchStart: this.onTouchStart.bind(this),
            touchMove: this.onTouchMove.bind(this),
            touchEnd: this.onTouchEnd.bind(this),
            contextMenu: this.onContextMenu.bind(this),
        };

        this.initializeInteractions();
    }

    /**
     * Initialize all event listeners
     */
    initializeInteractions() {
        // Mouse events
        this.canvas.addEventListener('mousedown', this.boundHandlers.mouseDown);
        this.canvas.addEventListener('mousemove', this.boundHandlers.mouseMove);
        this.canvas.addEventListener('mouseup', this.boundHandlers.mouseUp);
        this.canvas.addEventListener('wheel', this.boundHandlers.mouseWheel);
        this.canvas.addEventListener('dblclick', this.boundHandlers.doubleClick);

        // Touch events
        this.canvas.addEventListener('touchstart', this.boundHandlers.touchStart);
        this.canvas.addEventListener('touchmove', this.boundHandlers.touchMove);
        this.canvas.addEventListener('touchend', this.boundHandlers.touchEnd);

        // Prevent context menu
        this.canvas.addEventListener('contextmenu', this.boundHandlers.contextMenu);

        // Prevent default touch behaviors
        this.canvas.style.touchAction = 'none';
    }

    /**
     * Remove all event listeners
     */
    destroy() {
        this.canvas.removeEventListener('mousedown', this.boundHandlers.mouseDown);
        this.canvas.removeEventListener('mousemove', this.boundHandlers.mouseMove);
        this.canvas.removeEventListener('mouseup', this.boundHandlers.mouseUp);
        this.canvas.removeEventListener('wheel', this.boundHandlers.mouseWheel);
        this.canvas.removeEventListener('dblclick', this.boundHandlers.doubleClick);
        this.canvas.removeEventListener('touchstart', this.boundHandlers.touchStart);
        this.canvas.removeEventListener('touchmove', this.boundHandlers.touchMove);
        this.canvas.removeEventListener('touchend', this.boundHandlers.touchEnd);
        this.canvas.removeEventListener('contextmenu', this.boundHandlers.contextMenu);
    }

    /**
     * Mouse down handler - start pan
     * @param {MouseEvent} event - Mouse event
     */
    onMouseDown(event) {
        this.isDragging = true;
        this.lastMousePos = this.getMousePos(event);
        this.canvas.style.cursor = 'grabbing';
    }

    /**
     * Mouse move handler - pan viewport
     * @param {MouseEvent} event - Mouse event
     */
    onMouseMove(event) {
        if (!this.isDragging) {
            return;
        }

        const currentPos = this.getMousePos(event);
        const deltaX = currentPos.x - this.lastMousePos.x;
        const deltaY = currentPos.y - this.lastMousePos.y;

        this.viewport.panBy(deltaX, deltaY);
        this.lastMousePos = currentPos;

        this.triggerRedraw();
    }

    /**
     * Mouse up handler - stop pan
     * @param {MouseEvent} event - Mouse event
     */
    onMouseUp() {
        this.isDragging = false;
        this.canvas.style.cursor = 'grab';
    }

    /**
     * Mouse wheel handler - zoom
     * @param {WheelEvent} event - Wheel event
     */
    onMouseWheel(event) {
        event.preventDefault();

        const mousePos = this.getMousePos(event);
        // Calculate zoom factor based on wheel direction
        const zoomFactor =
            event.deltaY > 0 ? CANVAS_CONSTANTS.ZOOM_OUT_FACTOR : CANVAS_CONSTANTS.ZOOM_IN_FACTOR;

        this.viewport.zoomToPoint(zoomFactor, mousePos.x, mousePos.y);
        this.triggerRedraw();
    }

    /**
     * Double click handler - reset view
     * @param {MouseEvent} event - Mouse event
     */
    onDoubleClick() {
        this.viewport.resetView();
        this.triggerRedraw();
    }

    /**
     * Touch start handler
     * @param {TouchEvent} event - Touch event
     */
    onTouchStart(event) {
        event.preventDefault();

        // Handle single touch (dragging)
        if (event.touches.length === CANVAS_CONSTANTS.SINGLE_TOUCH) {
            // Single touch - start pan
            this.isDragging = true;
            this.lastMousePos = this.getTouchPos(event.touches[0]);
        } else if (event.touches.length === CANVAS_CONSTANTS.MULTI_TOUCH) {
            // Two touches - start pinch zoom
            this.isDragging = false;
            const gesture = this.detectPinchGesture(event);
            if (gesture) {
                this.lastTouchDistance = gesture.distance;
                this.lastTouchCenter = gesture.center;
            }
        }
    }

    /**
     * Touch move handler
     * @param {TouchEvent} event - Touch event
     */
    onTouchMove(event) {
        event.preventDefault();

        if (event.touches.length === 1 && this.isDragging) {
            // Single touch pan
            const currentPos = this.getTouchPos(event.touches[0]);
            const deltaX = currentPos.x - this.lastMousePos.x;
            const deltaY = currentPos.y - this.lastMousePos.y;

            this.viewport.panBy(deltaX, deltaY);
            this.lastMousePos = currentPos;

            this.triggerRedraw();
        } else if (event.touches.length === 2) {
            // Pinch zoom
            const gesture = this.detectPinchGesture(event);
            if (gesture && this.lastTouchDistance > 0) {
                const zoomFactor = gesture.distance / this.lastTouchDistance;

                this.viewport.zoomToPoint(zoomFactor, gesture.center.x, gesture.center.y);

                this.lastTouchDistance = gesture.distance;
                this.lastTouchCenter = gesture.center;

                this.triggerRedraw();
            }
        }
    }

    /**
     * Touch end handler
     * @param {TouchEvent} event - Touch event
     */
    onTouchEnd(event) {
        event.preventDefault();

        if (event.touches.length === 0) {
            this.isDragging = false;
            this.lastTouchDistance = 0;
        } else if (event.touches.length === 1) {
            // Switch back to pan mode
            this.isDragging = true;
            this.lastMousePos = this.getTouchPos(event.touches[0]);
            this.lastTouchDistance = 0;
        }
    }

    /**
     * Context menu handler - prevent
     * @param {Event} event - Context menu event
     */
    onContextMenu(event) {
        event.preventDefault();
    }

    /**
     * Get mouse position relative to canvas
     * @param {MouseEvent} event - Mouse event
     * @returns {Object} Position {x, y}
     */
    getMousePos(event) {
        const rect = this.canvas.getBoundingClientRect();
        return {
            x: event.clientX - rect.left,
            y: event.clientY - rect.top,
        };
    }

    /**
     * Get touch position relative to canvas
     * @param {Touch} touch - Touch object
     * @returns {Object} Position {x, y}
     */
    getTouchPos(touch) {
        const rect = this.canvas.getBoundingClientRect();
        return {
            x: touch.clientX - rect.left,
            y: touch.clientY - rect.top,
        };
    }

    /**
     * Detect pinch gesture from touch event
     * @param {TouchEvent} touchEvent - Touch event
     * @returns {Object|null} Gesture data or null
     */
    detectPinchGesture(touchEvent) {
        if (touchEvent.touches.length !== 2) {
            return null;
        }

        const touch1 = this.getTouchPos(touchEvent.touches[0]);
        const touch2 = this.getTouchPos(touchEvent.touches[1]);

        const distance = Math.sqrt(
            Math.pow(touch2.x - touch1.x, 2) + Math.pow(touch2.y - touch1.y, 2)
        );

        const center = {
            x: (touch1.x + touch2.x) / 2,
            y: (touch1.y + touch2.y) / 2,
        };

        return { distance, center, touch1, touch2 };
    }

    /**
     * Calculate distance between two points
     * @param {Object} point1 - First point {x, y}
     * @param {Object} point2 - Second point {x, y}
     * @returns {number} Distance
     */
    getDistance(point1, point2) {
        return Math.sqrt(Math.pow(point2.x - point1.x, 2) + Math.pow(point2.y - point1.y, 2));
    }

    /**
     * Get midpoint between two points
     * @param {Object} point1 - First point {x, y}
     * @param {Object} point2 - Second point {x, y}
     * @returns {Object} Midpoint {x, y}
     */
    getMidpoint(point1, point2) {
        return {
            x: (point1.x + point2.x) / 2,
            y: (point1.y + point2.y) / 2,
        };
    }

    /**
     * Trigger redraw (should be connected to main app)
     */
    triggerRedraw() {
        if (this.onRedrawNeeded) {
            this.onRedrawNeeded();
        }
    }

    /**
     * Set redraw callback
     * @param {Function} callback - Redraw callback function
     */
    setRedrawCallback(callback) {
        this.onRedrawNeeded = callback;
    }
}
